class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.predictiveSearchTrigger = this.querySelector('[data-search-button]');
    this.predictiveSearchCloseButton = this.querySelector('[data-close-button]');
    this.predictiveSearchContainer = this.querySelector('[data-search-container]');
    this.predictiveSearchPreviewContent = this.querySelector('[data-preview-search]');
    this.predictiveSearchSuggestedTerms = this.querySelector('[data-suggested-terms]');
    this.spinner = this.querySelector('[data-loading-spinner]');
    this.wash = this.querySelector('[data-wash]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Esc listener to close search
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.wash.addEventListener('click', this.close.bind(this));
    this.predictiveSearchCloseButton.addEventListener('click', this.close.bind(this));
    this.predictiveSearchTrigger.addEventListener('click', () => {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    this.searchTerm = newSearchTerm;

    if (this.searchTerm) {
      this.getSearchResults(this.searchTerm);
    } else {
      this.onFormReset();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    this.searchTerm = '';
    this.predictiveSearchResults.innerHTML = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.predictiveSearchPreviewContent?.setAttribute('aria-hidden', false);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    this.spinner.classList.remove('hidden');
    this.predictiveSearchResults.classList.add('opacity-50');
    this.predictiveSearchPreviewContent.classList.add('opacity-50');

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const params = new URLSearchParams();

    params.set('section_id', 'predictive-search-results');
    params.set('q', queryKey);
    params.set('resources[type]', 'query,product');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[limit]', '4');

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search-results');

        // Handle suggested terms being shown if no results are found
        if (resultsMarkup.querySelector('[data-no-results]') && this.predictiveSearchSuggestedTerms) {
          const resultsTermsContainer = resultsMarkup.querySelector('[data-popular-search-results]');
          resultsTermsContainer.innerHTML = this.predictiveSearchSuggestedTerms.innerHTML;
        }

        this.renderSearchResults(resultsMarkup.innerHTML);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('[data-predictive-search-status]');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.predictiveSearchResults.classList.remove('opacity-50');
    this.predictiveSearchPreviewContent.classList.remove('opacity-50');

    this.setLiveRegionResults();
    this.predictiveSearchPreviewContent?.setAttribute('aria-hidden', true);
    this.spinner.classList.add('hidden');
  }

  setLiveRegionResults() {
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.predictiveSearchContainer.setAttribute('aria-hidden', false);
    this.wash.setAttribute('aria-hidden', false);
    this.isOpen = true;
    trapFocus(this.predictiveSearchContainer, this.input);
  }

  close() {
    removeTrapFocus(this.predictiveSearchTrigger);
    this.predictiveSearchContainer.setAttribute('aria-hidden', true);
    this.wash.setAttribute('aria-hidden', true);

    // Using timeout to account for fade animations
    setTimeout(() => {
      this.closeResults();
    }, 300);

    this.isOpen = false;
  }

  closeResults() {
    this.input.value = '';

    this.searchTerm = '';
    this.predictiveSearchResults.innerHTML = '';
    this.predictiveSearchPreviewContent?.setAttribute('aria-hidden', false);
  }
}

customElements.define('predictive-search', PredictiveSearch);
